import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CircleNotch } from "@phosphor-icons/react";

const fmtMoney = (v) => (v == null || Number(v) === 0 ? "-" : `$${Number(v).toFixed(2)}`);
const fmtRmb = (v) => (v == null || Number(v) === 0 ? "-" : `¥${Number(v).toFixed(2)}`);
const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "-";
  }
};

export function ChinaQuoteDialog({ itemNo, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !itemNo) return;
    setLoading(true);
    setData(null);
    setError("");
    api
      .get(`/china-quote/${encodeURIComponent(itemNo)}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, [open, itemNo]);

  const product = data?.found ? data.product : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="china-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">China Quote Info · {itemNo}</DialogTitle>
          <DialogDescription>Live data from the Ningbo quotation system (Office / Admin only)</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-40 items-center justify-center text-zinc-400">
            <CircleNotch size={22} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="py-6 text-sm text-red-600" data-testid="china-quote-error">{error}</div>
        ) : data && !data.found ? (
          <div className="py-6 text-sm text-zinc-500" data-testid="china-quote-not-found">
            No quote record found for "{itemNo}" in the China quotation system.
          </div>
        ) : product ? (
          <div className="flex flex-col gap-3" data-testid="china-quote-content">
            <div className="flex gap-3">
              {product.photo_url && (
                <img
                  src={product.photo_url}
                  alt={product.item_number}
                  className="h-28 w-28 shrink-0 rounded-sm border border-zinc-200 bg-zinc-50 object-contain"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              <div className="min-w-0">
                <div className="text-base font-bold text-zinc-900" data-testid="china-quote-item-number">
                  {product.item_number || "-"}
                </div>
                <div className="mt-0.5 text-sm text-zinc-600">{product.description || "-"}</div>
              </div>
            </div>
            {product.price_lists.length > 0 ? (
              <div className="overflow-x-auto rounded-sm border border-zinc-200">
                <table className="w-full text-xs" data-testid="china-quote-price-table">
                  <thead className="bg-zinc-50">
                    <tr className="text-[10px] uppercase tracking-[0.04em] text-zinc-500">
                      <th className="px-2 py-1.5 text-left font-medium">Size</th>
                      <th className="px-2 py-1.5 text-right font-medium">QTY/HQ</th>
                      <th className="px-2 py-1.5 text-right font-medium">Price USD</th>
                      <th className="px-2 py-1.5 text-right font-medium">Price CAD</th>
                      <th className="px-2 py-1.5 text-right font-medium">RMB Cost</th>
                      <th className="px-2 py-1.5 text-right font-medium">Quote Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.price_lists.map((pl, i) => (
                      <tr key={i} className="border-t border-zinc-100 text-zinc-700">
                        <td className="px-2 py-1.5">{pl.size || "-"}</td>
                        <td className="px-2 py-1.5 text-right font-mono-data">
                          {pl.qty_per_hq ? Math.round(pl.qty_per_hq).toLocaleString() : "-"}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono-data">{fmtMoney(pl.price_usd)}</td>
                        <td className="px-2 py-1.5 text-right font-mono-data">{fmtMoney(pl.price_cad)}</td>
                        <td className="px-2 py-1.5 text-right font-mono-data font-semibold">{fmtRmb(pl.rmb_cost)}</td>
                        <td className="px-2 py-1.5 text-right">{fmtDate(pl.quote_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-zinc-500">No price list records.</div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function ChinaQuoteLink({ itemNo, className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!itemNo) return <span>-</span>;
  if (user?.role !== "admin" && user?.role !== "office") return <span className={className}>{itemNo}</span>;
  return (
    <>
      <span
        role="button"
        tabIndex={0}
        className={`cursor-pointer text-blue-600 underline-offset-2 hover:underline ${className}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        onKeyDown={(e) => e.key === "Enter" && setOpen(true)}
        title="View China quotation info"
        data-testid={`china-quote-link-${itemNo}`}
      >
        {itemNo}
      </span>
      {open && <ChinaQuoteDialog itemNo={itemNo} open={open} onOpenChange={setOpen} />}
    </>
  );
}
